import { z } from "zod";

// unauth users are always attached to the single "Cisco U. Free" offer, but the api
// still returns it as a full subscribed-offer record shaped like an authed entitlement.
const userSubscribedOffer = z
  .object({
    content_bundle_id: z.number(),
    content_bundle_name: z.string(),
    content_id: z.number(),
    content_type: z.string(),
    created_at: z.string().nullable(),
    end_date: z.string().nullable(),
    entitlement_id: z.number(),
    eol_date: z.string().nullable(),
    eos_date: z.string().nullable(),
    exam_id: z.number(),
    friendly_url: z.string(),
    ga_date: z.string(),
    global_product_name: z.string(),
    is_single: z.number(),
    order_id: z.string(),
    path_id: z.number(),
    product_id: z.number(),
    start_date: z.string().nullable(),
    subscription_id: z.string(),
  })
  .loose();

// GET /freeContent (ciscou-unauth-api) - list of content guids granted for free via promotions
export const freeContentResponse = z.object({
  userEntitlements: z.array(z.string()),
  userSubscribedOffers: z.array(userSubscribedOffer),
});

export type FreeContentResponse = z.infer<typeof freeContentResponse>;

// catalog_data is a large, variable content-catalog record; only type the fields the
// UI actually reads (see src/views/LandingPage/topicContent.ts TopicContentCard) and let
// the rest pass through untouched rather than re-modeling the whole catalog item shape here.
const globalProduct = z
  .object({
    content_bundle_id: z.union([z.number(), z.string()]).nullish(),
  })
  .loose();

const catalogData = z
  .object({
    created_at: z.string().nullish(),
    guid: z.string().nullish(),
    content_bundle: z.array(z.union([z.number(), z.string()])).nullish(),
    global_products: z.array(globalProduct).nullish(),
  })
  .loose();

const topicContentCard = z
  .object({
    object_guid: z.string(),
    score: z.number(),
    is_free: z.boolean(),
    is_new: z.boolean(),
    catalog_data: catalogData.nullish(),
  })
  .loose();

// GET /landing-content (ciscou-unauth-api) - topic-based homepage sections, gated by the
// ENABLE_UNAUTH_TOPIC_BASED_CONTENT statsig flag (see unauthLanding.vue fetchNewLandingContent)
export const landingContentResponse = z.object({
  free_content: z.array(topicContentCard),
  new_content: z.array(topicContentCard),
});

export type LandingContentResponse = z.infer<typeof landingContentResponse>;

const prescribedPrepMaterial = z
  .object({
    guid: z.string(),
    title: z.string(),
    type: z.string(),
  })
  .loose();

const examTopic = z
  .object({
    guid: z.string(),
    topic_name: z.string(),
  })
  .loose();

const certificationExam = z
  .object({
    guid: z.string(),
    exam_number: z.string(),
    title: z.string(),
    topics: z.array(examTopic).nullish(),
    prescribed_prep_material: z.array(prescribedPrepMaterial).nullish(),
  })
  .loose();

// GET /certifications/:id (ciscou-unauth-api) - single certification detail page
export const certificationDetailResponse = z
  .object({
    acronym: z.string(),
    active: z.number(),
    certification_level: z.string(),
    ce_credits: z.number(),
    description: z.string(),
    friendly_url: z.string(),
    guid: z.string(),
    id: z.number(),
    job_roles: z.array(z.string()).nullish(),
    name: z.string(),
    summary: z.string(),
    tag_line: z.string(),
    technology: z.array(z.string()).nullish(),
    type: z.string(),
    exam: z.array(certificationExam).nullish(),
  })
  .loose();

export type CertificationDetailResponse = z.infer<typeof certificationDetailResponse>;

// GET /certifications (ciscou-unauth-api) - list variant, items match certificationDetailResponse
export const certificationsListResponse = z.object({
  data: z.array(certificationDetailResponse),
  total_records: z.number().nullish(),
});

export type CertificationsListResponse = z.infer<typeof certificationsListResponse>;

// GET /communities (ciscou-unauth-api) - Salesforce community records
const community = z
  .object({
    Id: z.string(),
    Name: z.string(),
    Category: z.string(),
  })
  .loose();

export const communitiesResponse = z.array(community);

export type Community = z.infer<typeof community>;

const discussionThread = z
  .object({
    Id: z.string(),
    Title: z.string(),
    Body: z.string(),
  })
  .loose();

// GET /discussion-threads (ciscou-unauth-api) - wraps threads in a single-item community array
export const discussionThreadsResponse = z.array(
  z
    .object({
      Id: z.string(),
      Name: z.string(),
      Threads: z.array(discussionThread).nullish(),
    })
    .loose()
);

// POST /retrieveCatalogItems (ciscou-unauth-api) - heterogeneous catalog records (paths, practice
// exams, exam vouchers, labs, ...); `type` selects the shape, only `guid` is truly universal.
const retrievedCatalogItem = z
  .object({
    guid: z.string(),
    type: z.string().nullish(),
    exam_number: z.string().nullish(),
    prescribed_prep_material: z.array(prescribedPrepMaterial).nullish(),
  })
  .loose();

// GetCatalogItemsResponse.Data ([]map[string]interface{}, no omitempty) marshals as JSON null
// (not []) when zero items match - a nil Go slice always serializes as null.
export const catalogItemsResponse = z.object({
  Data: z.array(retrievedCatalogItem).nullish(),
});

export type RetrievedCatalogItem = z.infer<typeof retrievedCatalogItem>;

// GET /exam-voucher/:id (ciscou-unauth-api) - same underlying record as the "exam-voucher" type
// item returned by /retrieveCatalogItems, fetched singly instead of in a Data-wrapped list.
export const examVoucherResponse = z
  .object({
    guid: z.string(),
    type: z.string(),
    title: z.string(),
  })
  .loose();

export type ExamVoucherResponse = z.infer<typeof examVoucherResponse>;

// GET /courses/:id (ciscou-unauth-api) - shared by courses.ts fetch(), challengeContent.ts
// fetchChallenge(), and tutorialContent.ts fetchTutorial() (same endpoint, same record shape).
// content-catalog-service's Content struct declares FriendlyUrl as *string (no omitempty), so
// it genuinely serializes as null for content that hasn't been assigned a friendly url yet.
export const courseDetailResponse = z
  .object({
    guid: z.string(),
    type: z.string(),
    title: z.string(),
    friendly_url: z.string().nullish(),
    global_products: z.array(globalProduct).nullish(),
    active: z.number().nullish(),
    url: z.string().nullish(),
    tracks: z
      .array(z.object({ id: z.number(), guid: z.string(), type: z.string() }).loose())
      .nullish(),
    catalog_data: z.object({ url: z.string().nullish() }).loose().nullish(),
  })
  .loose();

export type CourseDetailResponse = z.infer<typeof courseDetailResponse>;

const trackCourse = z
  .object({
    guid: z.string(),
    url: z.string().nullish(),
    publisher_name: z.string().nullish(),
  })
  .loose();

const track = z
  .object({
    guid: z.string(),
    name: z.string(),
    Courses: z.array(trackCourse).nullish(),
  })
  .loose();

// GET /paths/:id (ciscou-unauth-api) - learning path detail with nested tracks/Courses
// content-catalog-service's Path struct declares FriendlyUrl as *string (no omitempty), same
// nullable-pointer pattern as Content.FriendlyUrl - can genuinely serialize as null.
export const learningPathResponse = z
  .object({
    guid: z.string(),
    type: z.string(),
    name: z.string(),
    friendly_url: z.string().nullish(),
    number_of_labs: z.number().nullish(),
    content_count: z.number().nullish(),
    number_of_assessments: z.number().nullish(),
    technology: z.string().nullish(),
    global_products: z.array(globalProduct).nullish(),
    tracks: z.array(track).nullish(),
  })
  .loose();

export type LearningPathResponse = z.infer<typeof learningPathResponse>;

// GET /collections/new and /collections/free (ciscou-unauth-api) - same catalog_data shape as
// the /landing-content topic cards, but keyed by guid/type at the top level instead of object_guid.
const collectionItem = z
  .object({
    guid: z.string(),
    type: z.string(),
    catalog_data: catalogData.nullish(),
  })
  .loose();

export const collectionResponse = z.object({
  key: z.string(),
  items: z.array(collectionItem),
});

export type CollectionResponse = z.infer<typeof collectionResponse>;

// POST /telemetry (ciscou-unauth-api) - matches real payloads sent from src/utils/telemetry.ts;
// intentionally NOT the same shape as telemetryService.ts's RequiredObject (that one requires
// factor[].interestedTopics, which real telemetry calls never send).
const telemetryFactor = z
  .object({
    type: z.string(),
  })
  .loose();

const telemetryObject = z
  .object({
    type: z.string(),
    objectId: z.string(),
    objectTitle: z.string(),
  })
  .loose();

export const telemetryRequestBody = z
  .object({
    action: z.string(),
    factor: z.array(telemetryFactor),
    object: telemetryObject,
  })
  .loose();

export type TelemetryRequestBody = z.infer<typeof telemetryRequestBody>;

// GET /system-messages/:id (ciscou-unauth-api, proxies to commerce-proxy) - a single flat
// record, NOT an array (confirmed via a real proxied response) - status/message/startDate/endDate
// are read by store/modules/commerce.ts and its consumers (App.vue, buySubscription.vue, etc.)
export const systemMessageResponse = z
  .object({
    id: z.string(),
    name: z.string(),
    status: z.string(),
    message: z.string(),
    messageType: z.string().nullish(),
    startDate: z.string(),
    endDate: z.string(),
  })
  .loose();

export type SystemMessageResponse = z.infer<typeof systemMessageResponse>;
